import type { FocusEvent } from 'react';
import React, { useMemo, useState } from 'react';
import get from 'lodash/get';
import { Dropdown as SDropdown } from 'semantic-ui-react';
import type { StrictDropdownProps } from 'semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown';

import { Badge } from '../../element/Badge/Badge';
import { IconButton } from '../../element/Button/IconButton';
import { Layout } from '../../layout/Layout/Layout';
import { LayoutItem } from '../../layout/Layout/LayoutItem';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Icon } from '../../element/Icon/Icon';
import type { RawDropdownDirectionType } from '../Dropdown/RawDropdown';
import { DirectionMap } from '../Dropdown/RawDropdown';

export type RawDropdownTreeValueType =
  string | number | null | Array<string | number | null>;

type FormattedDropdownOption = {
  disableSelection: boolean;
  key: string;
  hasChildren: boolean;
  parentId?: string;
  searchText: string;
  text: string;
  value: string;
};

export type RawDropdownTreeProps = {
  displayKey: string;
  direction?: RawDropdownDirectionType;
  disabled?: boolean;
  hideIcon?: boolean;
  id?: string;
  invalid?: boolean;
  multiple?: boolean;
  name: string;
  nestedKey: string;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (value: RawDropdownTreeValueType) => Promise<unknown> | void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  options: Array<Record<string, unknown>>;
  parentOperatesAsDropdownOnly?: boolean;
  placeholder?: string;
  value?: RawDropdownTreeValueType;
  valueKey: string;
};

export const RawDropdownTree = ({
  displayKey,
  direction,
  disabled = false,
  hideIcon,
  id,
  invalid = false,
  multiple = false,
  name,
  nestedKey,
  onBlur,
  onChange,
  onFocus,
  options,
  parentOperatesAsDropdownOnly = false,
  placeholder,
  value,
  valueKey,
}: RawDropdownTreeProps) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [searchValue, setSearchValue] = useState('');
  const dropdownOptions = useMemo(() => {
    const formatOptions = (
      acc: FormattedDropdownOption[],
      o: Record<string, string>,
      parentOption?: FormattedDropdownOption
    ) => {
      const display = get(o, displayKey);
      const v = get(o, valueKey);
      if (display) {
        const nestedOption =
          nestedKey &&
          (get(o, nestedKey) as unknown as Array<Record<string, string>>);

        const hasNestedOptions = nestedOption && Array.isArray(nestedOption);
        const parentId = parentOption?.value;
        const formattedOption = {
          key: v,
          value: v,
          text: display,
          parentId,
          searchText: `${
            parentOption?.text ? `${parentOption.text} -> ` : ''
          }${display}`,
          hasChildren: !!hasNestedOptions,
          disableSelection: !!(hasNestedOptions && !multiple),
        };
        acc.push(formattedOption);

        if (hasNestedOptions) {
          nestedOption.forEach(nestedO =>
            formatOptions(acc, nestedO, formattedOption)
          );
        }
      }

      return acc;
    };
    return options.reduce<FormattedDropdownOption[]>((acc, option) => {
      return formatOptions(acc, option as Record<string, string>);
    }, []);
  }, [options]);

  if (multiple && (value === undefined || value === null)) {
    value = [];
  }

  const getAllChildrenAndSelectedChildrenOfParent = (
    parentId: string | number,
    optionValues: RawDropdownTreeValueType
  ) => {
    if (!Array.isArray(optionValues)) {
      optionValues = [optionValues];
    }
    const totalChildrenOfParent = dropdownOptions.filter(
      opt => opt.parentId === parentId
    );

    const selectedChildrenOfParent = totalChildrenOfParent.filter(child => {
      return Array.isArray(optionValues) && optionValues.includes(child.value);
    });

    return [selectedChildrenOfParent, totalChildrenOfParent];
  };

  const displayOptions = dropdownOptions.map(opt => {
    const [selectedChildrenOfParent, totalChildrenOfParent] =
      getAllChildrenAndSelectedChildrenOfParent(
        opt.value,
        value === undefined ? null : value
      );
    const childItemShouldHide = opt.parentId && !expandedIds.has(opt.parentId);
    const parentItemShouldHide =
      selectedChildrenOfParent &&
      selectedChildrenOfParent.length > 0 &&
      selectedChildrenOfParent.length === totalChildrenOfParent?.length;

    return {
      className: AttrsHelper.formatClassname(
        (childItemShouldHide || parentItemShouldHide) && !searchValue
          ? 'hide-option'
          : '',
        `value-${opt.value}`
      ),
      text: opt.text,
      key: opt.key,
      value: opt.value,
      content: searchValue ? (
        <div>
          <Layout position="middle" data-test={`option-${opt.searchText}`}>
            <LayoutItem>{opt.searchText}</LayoutItem>
          </Layout>
        </div>
      ) : (
        <Layout>
          <LayoutItem fluid>
            <div className={AttrsHelper.formatClassname(opt.parentId)}>
              <Layout position="middle">
                {!!opt.hasChildren && (
                  <LayoutItem rightGutter="16px" topGutter="2px">
                    <span
                      data-test="expand-item"
                      onClick={e => {
                        e.stopPropagation();

                        if (expandedIds.has(opt.value)) {
                          expandedIds.delete(opt.value);
                        } else {
                          expandedIds.add(opt.value);
                        }
                        setExpandedIds(new Set(expandedIds));
                      }}
                    >
                      <Icon
                        name={
                          expandedIds.has(opt.value)
                            ? 'arrow-down'
                            : 'arrow-right'
                        }
                      />
                    </span>
                  </LayoutItem>
                )}
                <div
                  style={{
                    width: '100%',
                    cursor:
                      opt.disableSelection && !parentOperatesAsDropdownOnly
                        ? 'default'
                        : 'pointer',
                  }}
                  onClick={e => {
                    if (opt.disableSelection) e.stopPropagation();
                    if (parentOperatesAsDropdownOnly) {
                      if (expandedIds.has(opt.value)) {
                        expandedIds.delete(opt.value);
                      } else {
                        expandedIds.add(opt.value);
                      }
                      setExpandedIds(new Set(expandedIds));
                    }
                  }}
                >
                  <Layout
                    position="middle"
                    direction="horizontal"
                    name={`DROPDOWNTREE_LABEL_${opt.text}`}
                  >
                    <LayoutItem fluid leftGutter={opt.parentId ? '60px' : null}>
                      {opt.text}
                    </LayoutItem>
                    {selectedChildrenOfParent &&
                    selectedChildrenOfParent.length > 0 &&
                    !multiple ? (
                      <LayoutItem position="right">
                        <Badge background="blue" name="selected-children">
                          {selectedChildrenOfParent.length}
                        </Badge>
                      </LayoutItem>
                    ) : null}
                  </Layout>
                </div>
              </Layout>
            </div>
          </LayoutItem>
        </Layout>
      ),
    };
  });
  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : !!value || value === 0;

  const changeValue = (optionValue: RawDropdownTreeValueType) => {
    optionValue = Array.isArray(optionValue)
      ? [...new Set(optionValue)]
      : optionValue;
    void onChange(optionValue);
    setSearchValue('');
  };
  return (
    <div data-test={name}>
      <SDropdown
        disabled={disabled}
        error={invalid}
        fluid
        icon={
          !hasValue &&
          !hideIcon && (
            <IconButton
              pattern="secondary"
              variation="minimal"
              icon="arrow-down"
              name="default-dropdown-arrow"
              className="control-icon fixed-dimension-inline"
            />
          )
        }
        id={id}
        multiple={multiple}
        onBlur={e => {
          onBlur(e as FocusEvent<HTMLInputElement>);
          setSearchValue('');
        }}
        // @ts-expect-error This needs a new type
        onChange={(
          _e,
          { value: optionValue }: { value: RawDropdownTreeValueType }
        ) => {
          /**
           * For multiple options, we need to check if it has a new option and make sure
           * that if the option is a parent, that we replace it with all it's children
           * For single options, we need to make sure the parent was not selected
           */
          if (multiple) {
            if (
              Array.isArray(optionValue) &&
              Array.isArray(value) &&
              optionValue.length > value.length
            ) {
              const newlyAddedId = optionValue.find(
                optId => Array.isArray(value) && !value.includes(optId)
              );

              const newOption = dropdownOptions.find(
                opt => opt.value === newlyAddedId
              );

              if (newOption?.parentId) {
                changeValue(optionValue);
              } else if (!parentOperatesAsDropdownOnly) {
                const [selectedChildrenOfParent, totalChildrenOfParent] =
                  getAllChildrenAndSelectedChildrenOfParent(
                    newlyAddedId as string,
                    optionValue
                  );
                // Check if all children are selected for a parent. If so, remove all, else add all

                let newOptions = optionValue.filter(
                  optId => optId !== newlyAddedId
                );

                newOptions =
                  selectedChildrenOfParent &&
                  selectedChildrenOfParent.length ===
                    totalChildrenOfParent?.length
                    ? newOptions.filter(
                        optValue =>
                          !totalChildrenOfParent.some(
                            childOpt => childOpt.value === optValue
                          )
                      )
                    : [
                        ...newOptions,
                        ...(totalChildrenOfParent?.map(opt => opt.value) || []),
                      ];

                changeValue(newOptions);
              }
            } else {
              changeValue(optionValue);
            }
          } else if (optionValue) {
            const foundOption = dropdownOptions.find(
              opt => opt.value === optionValue
            );

            if (foundOption?.parentId) {
              changeValue(optionValue);
            }
          } else {
            changeValue(optionValue);
          }
        }}
        onFocus={event => {
          onFocus(event as FocusEvent<HTMLInputElement>);
        }}
        onSearchChange={(e, data) => {
          setSearchValue(data.searchQuery);
        }}
        options={displayOptions}
        placeholder={placeholder}
        renderLabel={({ text, key }) => {
          // TODO: remove this in Semantic v3
          // This maintains compatibility with Shorthand API in v1 as this might be called in "Label.create()"
          if (typeof text === 'function') {
            return text;
          }
          return {
            content: (
              <>
                {text}
                <span
                  className="pill-control-icon"
                  onClick={e => {
                    e.stopPropagation();
                    const foundPillItem = dropdownOptions.find(
                      op => op.key === key
                    );
                    void onChange(
                      (value as string[]).filter(
                        v => v !== foundPillItem?.value
                      )
                    );
                  }}
                >
                  <Icon name="x-mark" />
                </span>
              </>
            ),
          };
        }}
        search
        searchQuery={searchValue}
        selection
        selectOnBlur={false}
        selectOnNavigation={false}
        scrolling
        upward={DirectionMap[direction as keyof typeof DirectionMap]}
        value={value as StrictDropdownProps['value']}
      />
    </div>
  );
};
